import type { CallExpression, Expression, ObjectExpression, Program, Property } from "acorn"
import { parse } from "acorn"
import { simple } from "acorn-walk"

export type CodexStaticValue = string | number | boolean | null | CodexStaticValue[] | { [key: string]: CodexStaticValue }

export interface ParsedCodexOrchestrationCall {
    toolName: string
    detail?: string
    patch?: string
    input?: CodexStaticValue
}

export interface ParsedCodexOrchestration {
    calls: ParsedCodexOrchestrationCall[]
}

export function parseCodexOrchestration(source: string): ParsedCodexOrchestration | null {
    let program: Program
    try {
        program = parse(source, { ecmaVersion: "latest", sourceType: "module" })
    } catch {
        return null
    }

    const bindings = new Map<string, CodexStaticValue>()
    for (const statement of program.body) {
        if (statement.type !== "VariableDeclaration" || statement.kind !== "const") continue
        for (const declaration of statement.declarations) {
            if (declaration.id.type !== "Identifier") continue
            const value = staticValue(declaration.init)
            if (value.ok) bindings.set(declaration.id.name, value.value)
        }
    }

    const calls: ParsedCodexOrchestrationCall[] = []
    simple(program, {
        CallExpression(node) {
            const toolName = codexToolName(node.callee)
            if (!toolName) return

            const firstArgument = node.arguments[0]
            const staticArgument =
                firstArgument?.type === "Identifier"
                    ? staticBinding(bindings, firstArgument.name)
                    : firstArgument?.type === "SpreadElement"
                      ? { ok: false as const }
                      : staticValue(firstArgument)
            const input = staticArgument.ok ? staticArgument.value : undefined
            const detail = toolName === "exec_command" && isStaticRecord(input) && typeof input.cmd === "string" ? input.cmd : undefined

            calls.push({
                toolName,
                ...(detail !== undefined ? { detail } : {}),
                ...(toolName === "apply_patch" && typeof input === "string" ? { patch: input } : {}),
                ...(input !== undefined ? { input } : {}),
            })
        },
    })

    return { calls }
}

function codexToolName(callee: CallExpression["callee"]): string | undefined {
    if (callee.type !== "MemberExpression" || callee.computed) return undefined
    if (callee.object.type !== "Identifier" || callee.object.name !== "tools") return undefined
    return callee.property.type === "Identifier" ? callee.property.name : undefined
}

type StaticValueResult = { ok: true; value: CodexStaticValue } | { ok: false }

function staticBinding(bindings: Map<string, CodexStaticValue>, name: string): StaticValueResult {
    const value = bindings.get(name)
    return value === undefined ? { ok: false } : { ok: true, value }
}

function staticObjectValue(object: ObjectExpression): StaticValueResult {
    const entries: Array<[string, CodexStaticValue]> = []
    for (const property of object.properties) {
        if (property.type !== "Property" || property.kind !== "init" || property.computed || property.method) return { ok: false }
        const name = propertyName(property)
        if (name === undefined) return { ok: false }
        const value = staticValue(property.value)
        if (!value.ok) return value
        entries.push([name, value.value])
    }
    return { ok: true, value: Object.fromEntries(entries) }
}

function propertyName(property: Property): string | undefined {
    if (property.key.type === "Identifier") return property.key.name
    if (property.key.type === "Literal" && typeof property.key.value === "string") return property.key.value
    return undefined
}

function staticValue(expression: Expression | null | undefined): StaticValueResult {
    if (!expression) return { ok: false }
    if (expression.type === "Literal") {
        if (
            expression.value === null ||
            typeof expression.value === "string" ||
            typeof expression.value === "number" ||
            typeof expression.value === "boolean"
        ) {
            return { ok: true, value: expression.value }
        }
        return { ok: false }
    }
    if (expression.type === "TemplateLiteral") {
        if (expression.expressions.length > 0 || expression.quasis.length !== 1) return { ok: false }
        const value = expression.quasis[0]?.value
        const text = value?.cooked ?? value?.raw
        return text === undefined ? { ok: false } : { ok: true, value: text }
    }
    if (expression.type === "ObjectExpression") return staticObjectValue(expression)
    if (expression.type === "ArrayExpression") {
        const values: CodexStaticValue[] = []
        for (const element of expression.elements) {
            if (!element || element.type === "SpreadElement") return { ok: false }
            const value = staticValue(element)
            if (!value.ok) return value
            values.push(value.value)
        }
        return { ok: true, value: values }
    }
    if (expression.type === "UnaryExpression" && (expression.operator === "+" || expression.operator === "-") && expression.argument.type === "Literal") {
        const value = expression.argument.value
        if (typeof value !== "number") return { ok: false }
        return { ok: true, value: expression.operator === "-" ? -value : value }
    }
    if (expression.type === "UnaryExpression" && expression.operator === "!") {
        const value = staticValue(expression.argument)
        if (!value.ok) return value
        return { ok: true, value: !value.value }
    }
    return { ok: false }
}

function isStaticRecord(value: CodexStaticValue | undefined): value is { [key: string]: CodexStaticValue } {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}
